import {
  useFrameChromeConfig,
  type FrameChromeConfig,
  type FrameChromeMenuItem,
  type FrameChromeTool,
} from "@/components/shapes/shared/frame-chrome-config";
import { FrameChromeTools } from "@/components/shapes/shared/frame-chrome-tools";
import { useFrameChromeHidden } from "@/components/shapes/shared/frame-renderer-context";
import { FrameCalmTitle } from "@/components/shapes/shared/frame-calm-title";
import { openTaskDetails } from "@/lib/right-panel-navigation";
import { githubRepository } from "./github-repository";
import { CONCEPT_ICONS } from "@/components/ui";
import { useCallback, useMemo, useState } from "react";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { useProjectStore } from "@/stores/project-store";
import { updateFrame } from "@/api/spaces";
import { updateTaskStatus } from "@/api/tasks";
import { getCurrentDenkerWs } from "@/api/canvas-ws-ref";
import { useT } from "@/i18n";
import { framesGithubMessages } from "@/i18n/messages/frames-github";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useTasksStore } from "@/stores/tasks-store";
import { useNotificationStore } from "@/stores/notification-store";
import { CommitsSection } from "./commits-section";
import { commitsFrom, pullRequestsFrom } from "./github-content";
import { PrSection } from "./pr-section";
import { ActionsSection, type ActionRun } from "./actions-section";
import { BranchPicker } from "./branch-picker";
import { useFrameSurface } from "../shared/use-frame-surface";
import { applyFramePatch } from "@/lib/frame-ingest";
import {
  CARD_ROWS_INSET,
  CARD_TITLE_INSET,
} from "../shared/frame-content-padding";

export function GithubFrame({ id, data, selected }: FrameProps) {
  const t = useT(framesGithubMessages);
  const tShared = useT(framesSharedMessages);
  const frameSurface = useFrameSurface(id);
  // On the wall's Pinned tiles and in an opened window a calm title line
  // replaces the branch row; the window's pill hosts the branch picker.
  const chromeHidden = useFrameChromeHidden();
  const d = data;
  const branch = (d.content?.branch as string) ?? "";
  const commits = commitsFrom(d.content?.commits);
  const prs = pullRequestsFrom(d.content?.pr);
  const actions = (d.content?.actions as ActionRun[]) ?? [];

  const currentProject = useProjectStore((s) => s.currentProject);
  const locked = !!d.taskTicket;
  const taskId = d.taskTicket?.id ?? null;

  // Read the linked task's record so we can surface task-driven branch info
  // and the assigned-agent chip when the frame is task-bound.
  const linkedTask = useTasksStore((s) =>
    taskId ? (s.tasks.find((task) => task.id === taskId) ?? null) : null,
  );
  const displayBranch = linkedTask?.branch_name || branch;

  const handleSelectBranch = useCallback(
    (newBranch: string) => {
      // Compare against displayBranch (what BranchPicker is showing),
      // not the raw frame branch. In task-bound mode displayBranch is
      // linkedTask.branch_name, so `newBranch === branch` would let a
      // no-op selection through and trigger an unnecessary update.
      if (!newBranch || newBranch === displayBranch) return;
      const { currentProject: proj, currentSpace } = useProjectStore.getState();
      if (!proj || !currentSpace) return;

      const mergedContent = { ...(d.content ?? {}), branch: newBranch };

      // Optimistic local update, then persist.
      applyFramePatch(id, { content: mergedContent });
      updateFrame(proj.id, currentSpace.id, id, {
        content: mergedContent,
      }).catch(() => {
        // Revert on failure
        applyFramePatch(id, { content: d.content ?? {} });
      });

      // Ask the companion to re-read commits/PR/CI for the picked branch
      // (viewer mode — no `git checkout`). The backend relays it only for
      // a project this socket subscribes to, so it takes the project id.
      getCurrentDenkerWs()?.sendGithubFrameViewBranch(proj.id, id, newBranch);
    },
    [id, displayBranch, d.content],
  );

  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const handleDiscardClick = useCallback(() => {
    if (!taskId || !currentProject) return;
    setShowDiscardConfirm(true);
  }, [taskId, currentProject]);

  const handleDiscardConfirm = useCallback(async () => {
    setShowDiscardConfirm(false);
    if (!taskId || !currentProject) return;
    try {
      await updateTaskStatus(currentProject.id, taskId, "backlog");
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("unknownError");
      useNotificationStore.getState().addSystemToast({
        id: `discard-branch-fail-${Date.now()}`,
        level: "error",
        title: t("discardFailed"),
        description: msg,
      });
    }
  }, [taskId, currentProject, t]);

  const repository = githubRepository([
    ...prs.map((pr) => pr.url),
    ...actions.map((run) => run.url),
  ]);
  // An opened window's ⋯ offers Open task for every task's frame (the
  // shared item menu), so the GitHub frame adds only Discard branch there.
  const discardItems = useMemo<FrameChromeMenuItem[]>(
    () =>
      taskId
        ? [
            {
              id: "discard",
              label: t("discardBranch"),
              icon: Icons.Trash2,
              destructive: true,
              onSelect: handleDiscardClick,
            },
          ]
        : [],
    [taskId, handleDiscardClick, t],
  );
  const taskItems = useMemo<FrameChromeMenuItem[]>(
    () =>
      taskId
        ? [
            {
              id: "open-task",
              label: tShared("openTask"),
              icon: CONCEPT_ICONS.task,
              onSelect: () => {
                void openTaskDetails(taskId);
              },
            },
            ...discardItems,
          ]
        : [],
    [taskId, discardItems, tShared],
  );
  const projectId = currentProject?.id ?? null;
  // An opened window's pill hosts the branch picker, its ⋯ the task items.
  const chromeConfig = useMemo<FrameChromeConfig>(
    () => ({
      tools: [
        {
          kind: "custom",
          id: "github-branch",
          render: (onOpenChange) => (
            <BranchPicker
              projectId={projectId}
              currentBranch={displayBranch}
              onSelect={handleSelectBranch}
              locked={locked}
              onOpenChange={onOpenChange}
            />
          ),
        },
      ],
      menuItems: discardItems,
    }),
    [displayBranch, handleSelectBranch, locked, projectId, discardItems],
  );
  const cardFace = useFrameChromeConfig(chromeConfig) || chromeHidden;
  const inlineTools = useMemo<FrameChromeTool[]>(
    () =>
      taskItems.length > 0
        ? [
            {
              kind: "menu",
              id: "github-more",
              label: t("more"),
              icon: Icons.MoreHorizontal,
              items: taskItems,
            },
          ]
        : [],
    [taskItems, t],
  );

  return (
    <>
      <div
        className={cn(
          frameSurface.className,
          "relative flex h-full flex-col overflow-hidden",
          selected
            ? "border-[var(--frame-selected-stroke)]"
            : "border-glass-stroke",
        )}
        data-testid="github-frame"
        {...frameSurface.attributes}
      >
        {cardFace ? (
          <FrameCalmTitle
            title={repository?.name || data.title?.trim() || "GitHub"}
            className={CARD_TITLE_INSET}
            data-testid="github-frame-top-row"
          />
        ) : null}
        <div
          className={cn(
            "scrollbar-none nodrag min-h-0 flex-1 overflow-auto",
            "[--denk-frame-content-padding-block-start:12px] [--denk-frame-content-padding-block-end:12px] [--denk-frame-content-padding-inline:12px]",
            cardFace && CARD_ROWS_INSET,
            selected && "nowheel",
          )}
          data-frame-content="true"
        >
          {cardFace ? null : (
            <div
              className="mb-3.5 flex min-h-6 items-center justify-between gap-2"
              data-testid="github-frame-branch-row"
            >
              {repository && (
                <span
                  className="min-w-0 truncate text-appkit-body font-medium text-primary"
                  data-testid="github-frame-repository"
                >
                  {repository.name}
                </span>
              )}
              <div className="ml-auto min-w-0">
                <BranchPicker
                  projectId={projectId}
                  currentBranch={displayBranch}
                  onSelect={handleSelectBranch}
                  locked={locked}
                />
              </div>
              <FrameChromeTools tools={inlineTools} />
            </div>
          )}
          <PrSection prs={prs} />
          <ActionsSection actions={actions} />
          <CommitsSection
            key={displayBranch}
            commits={commits}
            repositoryUrl={repository?.url}
          />
          {!prs.length && !actions.length && !commits.length && (
            <p className="px-2 text-appkit-body text-muted">
              {t("emptyState")}
            </p>
          )}
        </div>
      </div>
      {showDiscardConfirm && (
        <ConfirmDialog
          title={t("discardBranchTitle")}
          description={t("discardBranchDescription")}
          confirmLabel={t("discard")}
          variant="danger"
          icon={<Icons.Trash2 className="h-3.5 w-3.5 text-status-error" />}
          onConfirm={handleDiscardConfirm}
          onCancel={() => setShowDiscardConfirm(false)}
          data-testid="github-frame-discard-confirm"
        />
      )}
    </>
  );
}
